import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Plus, Trash2, X, Paperclip, Filter, FileSpreadsheet, FileText, Pencil, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { INVOICE_CATEGORIES } from "@/lib/adminConfig";
import { exportInvoiceReport } from "@/lib/dataExport";
import { VEHICLE_ICON_LIST } from "@/lib/fleet";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const inputCls = "w-full bg-secondary/60 border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-[#17A2B8]";
const COLORS = ["#17A2B8", "#F5821F", "#10B981", "#8B5CF6", "#EF4444", "#3B82F6", "#F59E0B"];
const fileUrl = (path) => `${BACKEND_URL}/api/files/${path}`;
const money = (n) => `${Math.round(n || 0).toLocaleString("fr-FR")} XPF`;

export const Field = ({ label, children }) => (
  <label className="block mb-3"><span className="label-mono block mb-1.5">{label}</span>{children}</label>
);
export const DelBtn = ({ onClick }) => (
  <button onClick={onClick} className="text-muted-foreground hover:text-destructive p-1.5 rounded hover:bg-destructive/20 transition-colors"><Trash2 className="w-4 h-4" /></button>
);

export function Modal({ title, onClose, onSubmit, children, submitLabel = "Enregistrer" }) {
  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="glass rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 animate-slidein" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4"><h3 className="font-bold text-lg">{title}</h3><button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button></div>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>
          {children}
          <button type="submit" data-testid="modal-submit" className="w-full mt-2 bg-[#17A2B8] hover:bg-[#1391a5] text-white font-semibold py-2.5 rounded-md text-sm transition-colors">{submitLabel}</button>
        </form>
      </div>
    </div>
  );
}

function FieldInput({ f, value, onChange, refs = {} }) {
  const opts = f.options || refs[f.optionsRef] || [];
  if (f.type === "textarea") return <textarea className={inputCls} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`f-${f.key}`} />;
  if (f.type === "select") return (
    <select className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`f-${f.key}`}>
      <option value="">—</option>
      {opts.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  );
  if (f.type === "iconpicker") return (
    <div className="grid grid-cols-6 sm:grid-cols-8 gap-1.5" data-testid={`f-${f.key}`}>
      {VEHICLE_ICON_LIST.map((ic) => {
        const Ic = ic.Comp; const sel = value === ic.key;
        return (
          <button type="button" key={ic.key} title={ic.label} onClick={() => onChange(ic.key)} data-testid={`icon-opt-${ic.key}`}
            className={`flex items-center justify-center py-2 rounded-md border transition-colors ${sel ? "border-[#17A2B8] bg-[#17A2B8]/20 text-[#2ec5db]" : "border-border text-muted-foreground hover:text-foreground hover:bg-black/5"}`}>
            <Ic className="w-5 h-5" />
          </button>
        );
      })}
    </div>
  );
  if (f.type === "color") return <input type="color" className={inputCls + " h-[38px] p-1"} value={value || "#17A2B8"} onChange={(e) => onChange(e.target.value)} data-testid={`f-${f.key}`} />;
  return <input type={f.type === "number" ? "number" : f.type === "date" ? "date" : f.type === "password" ? "password" : "text"} className={inputCls} value={value ?? ""} onChange={(e) => onChange(f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} data-testid={`f-${f.key}`} />;
}

const cellRender = (f, v, refs = {}) => {
  const opts = f.options || refs[f.optionsRef] || [];
  if (v == null || v === "") return <span className="text-muted-foreground">—</span>;
  if (f.type === "color") return <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ background: v }} /> {v}</span>;
  if (f.type === "iconpicker") { const ic = VEHICLE_ICON_LIST.find((x) => x.key === v); if (ic) { const Ic = ic.Comp; return <span className="flex items-center gap-1.5"><Ic className="w-4 h-4 brand-teal" /> {ic.label}</span>; } return v; }
  if (f.type === "select") return opts.find((o) => o.v === v)?.l || v;
  return String(v);
};

// Generic schema-driven CRUD table. Supports idKey, separate create/edit fields and dynamic select refs.
export function AdminCrud({ endpoint, title, description, fields, columns, idKey = "id", createFields, editFields, refs = {} }) {
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const addF = createFields || fields;
  const editF = editFields || fields;
  const cols = columns || addF.filter((f) => f.type !== "textarea" && f.type !== "password");
  const activeFields = editId ? editF : addF;

  const load = useCallback(async () => {
    try { const r = await api.get(endpoint); setRows(r.data); } catch { setRows([]); }
  }, [endpoint]);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditId(null); setForm({}); setModal(true); };
  const openEdit = (r) => { setEditId(r[idKey]); setForm({ ...r }); setModal(true); };
  const submit = async () => {
    try {
      editId ? await api.put(`${endpoint}/${editId}`, form) : await api.post(endpoint, form);
      toast.success(editId ? "Modifié" : "Créé"); setModal(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const del = async (id) => { if (!window.confirm("Confirmer la suppression ?")) return; try { await api.delete(`${endpoint}/${id}`); toast.success("Supprimé"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div><h2 className="text-lg font-bold">{title}</h2>{description && <p className="text-sm text-muted-foreground">{description}</p>}</div>
        <button onClick={openAdd} data-testid="crud-add" className="flex items-center gap-2 bg-[#F5821F] hover:bg-[#e0741a] text-white font-semibold px-4 py-2 rounded-md text-sm transition-colors"><Plus className="w-4 h-4" /> Ajouter</button>
      </div>
      <div className="border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50"><tr className="label-mono text-left">{cols.map((c) => <th key={c.key} className="px-4 py-3">{c.label}</th>)}<th /></tr></thead>
          <tbody className="data">
            {rows.length === 0 && <tr><td colSpan={cols.length + 1} className="px-4 py-8 text-center text-muted-foreground">Aucune donnée</td></tr>}
            {rows.map((r) => (
              <tr key={r[idKey]} className="border-t border-border hover:bg-black/5 cursor-pointer" data-testid={`row-${r[idKey]}`} onClick={() => openEdit(r)}>
                {cols.map((c) => <td key={c.key} className="px-4 py-3">{cellRender((addF.find((f) => f.key === c.key) || editF.find((f) => f.key === c.key) || c), r[c.key], refs)}</td>)}
                <td className="px-4 py-3 text-right"><DelBtn onClick={(e) => { e.stopPropagation(); del(r[idKey]); }} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modal && (
        <Modal title={`${editId ? "Modifier" : "Nouveau"} — ${title}`} onClose={() => setModal(false)} onSubmit={submit}>
          {activeFields.map((f) => <Field key={f.key} label={f.label}><FieldInput f={f} value={form[f.key]} onChange={(v) => setForm((s) => ({ ...s, [f.key]: v }))} refs={refs} /></Field>)}
        </Modal>
      )}
    </div>
  );
}

// Read-only table backed by an existing endpoint
export function ReadTable({ endpoint, title, description, columns }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(endpoint).then((r) => setRows(r.data)).catch(() => setRows([])); }, [endpoint]);
  return (
    <div>
      <div className="mb-3"><h2 className="text-lg font-bold">{title}</h2>{description && <p className="text-sm text-muted-foreground">{description}</p>}</div>
      <div className="border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50"><tr className="label-mono text-left">{columns.map((c) => <th key={c.key} className="px-4 py-3">{c.label}</th>)}</tr></thead>
          <tbody className="data">
            {rows.length === 0 && <tr><td colSpan={columns.length} className="px-4 py-8 text-center text-muted-foreground">Aucune donnée</td></tr>}
            {rows.map((r, i) => <tr key={r.id || r.user_id || i} className="border-t border-border"><>{columns.map((c) => <td key={c.key} className="px-4 py-3">{c.render ? c.render(r) : (r[c.key] ?? "—")}</td>)}</></tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Simple key-value settings form for a settings group
export function SettingsForm({ group, title, description, fields }) {
  const [form, setForm] = useState({});
  useEffect(() => { api.get(`/settings/${group}`).then((r) => setForm(r.data || {})).catch(() => {}); }, [group]);
  const save = async () => { try { await api.put(`/settings/${group}`, form); toast.success("Paramètres enregistrés"); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } };
  return (
    <div className="max-w-lg">
      <div className="mb-3"><h2 className="text-lg font-bold">{title}</h2>{description && <p className="text-sm text-muted-foreground">{description}</p>}</div>
      {fields.map((f) => <Field key={f.key} label={f.label}><FieldInput f={f} value={form[f.key]} onChange={(v) => setForm((s) => ({ ...s, [f.key]: v }))} /></Field>)}
      <button onClick={save} data-testid="settings-save" className="mt-2 bg-[#17A2B8] hover:bg-[#1391a5] text-white font-semibold px-5 py-2.5 rounded-md text-sm">Enregistrer</button>
    </div>
  );
}

export const EditBtn = ({ onClick, testid }) => (
  <button onClick={onClick} data-testid={testid} title="Modifier" className="text-muted-foreground hover:text-[#17A2B8] p-1.5 rounded hover:bg-[#17A2B8]/20 transition-colors"><Pencil className="w-4 h-4" /></button>
);

export function InvoiceForm({ vehicles, defaultVehicleId, invoice, onSaved, onClose }) {
  const [form, setForm] = useState(invoice
    ? { vehicle_id: invoice.vehicle_id || "", category: invoice.category || "maintenance", amount: invoice.amount ?? "", date: invoice.date || "", supplier: invoice.supplier || "", invoice_number: invoice.invoice_number || "", notes: invoice.notes || "", file_path: invoice.file_path, file_name: invoice.file_name }
    : { vehicle_id: defaultVehicleId || "", category: "maintenance", amount: "", date: "", supplier: "", invoice_number: "", notes: "" });
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const parseDoc = async (file) => {
    if (!file) return;
    setAnalyzing(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/invoices/parse-document", fd);
      const d = r.data || {};
      setForm((s) => ({
        ...s,
        supplier: d.supplier || s.supplier,
        amount: d.amount != null && d.amount !== 0 ? d.amount : s.amount,
        date: d.date || s.date,
        invoice_number: d.invoice_number || s.invoice_number,
        category: d.category || s.category,
        vehicle_id: (d.vehicle_id && vehicles.some((v) => v.id === d.vehicle_id)) ? d.vehicle_id : s.vehicle_id,
        file_path: d.file_path || s.file_path,
        file_name: d.file_name || s.file_name,
      }));
      const found = [d.supplier && "fournisseur", d.amount && "montant", d.date && "date", d.invoice_number && "n°", d.category && "catégorie"].filter(Boolean);
      toast.success(`IA : ${found.length ? found.join(", ") + " remplis" : "document analysé"}`);
      if (d.plate && !d.vehicle_id) toast.info(`Plaque « ${d.plate} » non trouvée — choisissez le véhicule.`);
      else if (!form.vehicle_id && !d.vehicle_id) toast.info("Sélectionnez le véhicule concerné.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'analyse IA");
    } finally { setAnalyzing(false); }
  };
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file); const r = await api.post("/upload", fd); set("file_path", r.data.path); set("file_name", r.data.name); toast.success("Pièce jointe ajoutée"); }
    catch { toast.error("Échec de l'envoi du fichier"); } finally { setUploading(false); }
  };
  const submit = async () => {
    if (!form.vehicle_id) return toast.error("Choisir un véhicule");
    try {
      const body = { ...form, amount: Number(form.amount || 0) };
      if (invoice?.id) { await api.put(`/invoices/${invoice.id}`, body); toast.success("Facture modifiée"); }
      else { await api.post("/invoices", body); toast.success("Facture ajoutée"); }
      onSaved && onSaved(); onClose && onClose();
    }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <Modal title={invoice ? "Modifier la facture" : "Nouvelle facture"} onClose={onClose} onSubmit={submit}>
      <div className="mb-3 rounded-lg border border-[#F5821F]/40 bg-[#F5821F]/10 p-3" data-testid="inv-ai-zone">
        <div className="flex items-center gap-2 mb-1.5">
          <Sparkles className="w-4 h-4 text-[#F5821F]" />
          <span className="text-sm font-semibold">Remplir automatiquement avec l'IA</span>
        </div>
        <p className="text-xs text-muted-foreground mb-2">Importez la facture (PDF ou photo) : l'IA lit le document et pré-remplit les champs. Vérifiez avant d'enregistrer.</p>
        <label className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold cursor-pointer transition-colors ${analyzing ? "bg-secondary text-muted-foreground" : "bg-[#F5821F] hover:bg-[#e0741a] text-white"}`}>
          {analyzing ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyse en cours…</> : <><Sparkles className="w-4 h-4" /> Analyser une facture</>}
          <input type="file" accept=".pdf,image/*" disabled={analyzing} onChange={(e) => { const f = e.target.files[0]; e.target.value = ""; parseDoc(f); }} data-testid="inv-ai-file" className="hidden" />
        </label>
        {form.file_name && <span className="text-xs text-[#2ec5db] ml-3 inline-flex items-center gap-1"><Paperclip className="w-3 h-3" />{form.file_name}</span>}
      </div>
      <Field label="Véhicule *"><select className={inputCls} value={form.vehicle_id} onChange={(e) => set("vehicle_id", e.target.value)} data-testid="inv-vehicle"><option value="">—</option>{vehicles.map((v) => <option key={v.id} value={v.id}>{v.name} ({v.plate})</option>)}</select></Field>
      <Field label="Catégorie"><select className={inputCls} value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="inv-category">{INVOICE_CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}</select></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Montant (XPF)"><input type="number" className={inputCls} value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="inv-amount" /></Field>
        <Field label="Date"><input type="date" className={inputCls} value={form.date ? form.date.slice(0, 10) : ""} onChange={(e) => set("date", e.target.value)} data-testid="inv-date" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Fournisseur"><input className={inputCls} value={form.supplier} onChange={(e) => set("supplier", e.target.value)} data-testid="inv-supplier" /></Field>
        <Field label="N° facture"><input className={inputCls} value={form.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} data-testid="inv-number" /></Field>
      </div>
      <Field label="Notes"><textarea className={inputCls} rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="inv-notes" /></Field>
      <Field label="Pièce jointe (PDF / photo)">
        <input type="file" accept=".pdf,image/*" onChange={(e) => upload(e.target.files[0])} data-testid="inv-file" className="text-xs" />
        {uploading && <span className="text-xs text-muted-foreground ml-2">Envoi…</span>}
        {form.file_name && <span className="text-xs text-[#2ec5db] ml-2 flex items-center gap-1"><Paperclip className="w-3 h-3" />{form.file_name}</span>}
      </Field>
    </Modal>
  );
}

// Per-vehicle invoices list + add
export function InvoiceModal({ vehicle, onClose }) {
  const [rows, setRows] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [category, setCategory] = useState("");
  const load = useCallback(() => {
    const qs = new URLSearchParams({ vehicle_id: vehicle.id });
    if (dateFrom) qs.set("date_from", dateFrom);
    if (dateTo) qs.set("date_to", dateTo);
    if (category) qs.set("category", category);
    return api.get(`/invoices?${qs}`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [vehicle.id, dateFrom, dateTo, category]);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { if (!window.confirm("Supprimer cette facture ?")) return; await api.delete(`/invoices/${id}`); load(); };
  const total = rows.reduce((s, r) => s + (r.amount || 0), 0);
  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="glass rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 animate-slidein" onClick={(e) => e.stopPropagation()} data-testid="invoice-modal">
        <div className="flex items-center justify-between mb-4">
          <div><p className="label-mono">Factures — {vehicle.name}</p><p className="font-bold text-lg">{money(total)} · {rows.length} facture(s)</p></div>
          <div className="flex items-center gap-2">
            <button onClick={() => setAdding(true)} data-testid="invoice-add-btn" className="flex items-center gap-1.5 bg-[#F5821F] hover:bg-[#e0741a] text-white font-semibold px-3 py-1.5 rounded-md text-sm"><Plus className="w-4 h-4" /> Facture</button>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="border border-border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/50"><tr className="label-mono text-left"><th className="px-3 py-2">Date</th><th className="px-3 py-2">Catégorie</th><th className="px-3 py-2">Fournisseur</th><th className="px-3 py-2">Montant</th><th className="px-3 py-2">PJ</th><th /></tr></thead>
            <tbody className="data">
              {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">Aucune facture</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid={`invoice-row-${r.id}`}>
                  <td className="px-3 py-2">{r.date ? r.date.slice(0, 10) : "—"}</td>
                  <td className="px-3 py-2">{INVOICE_CATEGORIES.find((c) => c.v === r.category)?.l || r.category}</td>
                  <td className="px-3 py-2">{r.supplier || "—"}</td>
                  <td className="px-3 py-2 font-semibold">{money(r.amount)}</td>
                  <td className="px-3 py-2">{r.file_path ? <a href={fileUrl(r.file_path)} target="_blank" rel="noreferrer" className="text-[#2ec5db] flex items-center gap-1"><Paperclip className="w-3.5 h-3.5" /> Voir</a> : "—"}</td>
                  <td className="px-3 py-2 text-right"><div className="flex items-center justify-end gap-1"><EditBtn onClick={() => setEditing(r)} testid={`invoice-edit-${r.id}`} /><DelBtn onClick={() => del(r.id)} /></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {adding && <InvoiceForm vehicles={[vehicle]} defaultVehicleId={vehicle.id} onSaved={load} onClose={() => setAdding(false)} />}
        {editing && <InvoiceForm vehicles={[vehicle]} defaultVehicleId={vehicle.id} invoice={editing} onSaved={load} onClose={() => setEditing(null)} />}
      </div>
    </div>
  );
}

// Global billing dashboard (Facturation)
export function InvoiceDashboard() {
  const yr = new Date().getFullYear();
  const todayStr = new Date().toISOString().slice(0, 10);
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [dateFrom, setDateFrom] = useState(`${yr}-01-01`);
  const [dateTo, setDateTo] = useState(todayStr);
  const [companyId, setCompanyId] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [category, setCategory] = useState("");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const qs = new URLSearchParams();
    if (dateFrom) qs.set("date_from", dateFrom);
    if (dateTo) qs.set("date_to", dateTo);
    if (companyId) qs.set("company_id", companyId);
    if (vehicleId) qs.set("vehicle_id", vehicleId);
    if (category) qs.set("category", category);
    const [s, l] = await Promise.all([api.get(`/invoices/stats?${qs}`), api.get(`/invoices?${qs}`)]);
    setStats(s.data); setRows(l.data);
  }, [dateFrom, dateTo, companyId, vehicleId, category]);
  useEffect(() => { api.get("/vehicles").then((r) => setVehicles(r.data)); api.get("/companies").then((r) => setCompanies(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => { if (!window.confirm("Supprimer cette facture ?")) return; await api.delete(`/invoices/${id}`); load(); };
  const visibleRows = companyId ? rows.filter((r) => vehicles.find((v) => v.id === r.vehicle_id)?.company_id === companyId) : rows;
  const catData = (stats?.by_category || []).map((c, i) => ({ name: INVOICE_CATEGORIES.find((x) => x.v === c.category)?.l || c.category, value: c.total, fill: COLORS[i % COLORS.length] }));
  const total = stats?.total || 0;
  const filterLabel = [companyId && companies.find((c) => c.id === companyId)?.name, vehicleId && vehicles.find((v) => v.id === vehicleId)?.name, category && INVOICE_CATEGORIES.find((c) => c.v === category)?.l].filter(Boolean).join(" · ");
  const doExport = (fmt) => { if (!visibleRows.length) return toast.error("Aucune facture à exporter"); exportInvoiceReport({ period: { from: dateFrom, to: dateTo }, filterLabel }, stats, visibleRows, fmt); };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div><h2 className="text-lg font-bold">Coûts &amp; Factures</h2><p className="text-sm text-muted-foreground">Ajoutez des factures par famille et suivez les coûts par période.</p></div>
        <div className="flex items-center gap-2">
          <button onClick={() => doExport("csv")} data-testid="billing-export-csv" className="flex items-center gap-1.5 border border-border hover:border-[#17A2B8] px-3 py-2 rounded-md text-sm"><FileSpreadsheet className="w-4 h-4" /> CSV</button>
          <button onClick={() => doExport("pdf")} data-testid="billing-export-pdf" className="flex items-center gap-1.5 border border-border hover:border-[#17A2B8] px-3 py-2 rounded-md text-sm"><FileText className="w-4 h-4" /> PDF</button>
          <button onClick={() => setAdding(true)} data-testid="billing-add-invoice" className="flex items-center gap-1.5 bg-[#F5821F] hover:bg-[#e0741a] text-white font-semibold px-3 py-2 rounded-md text-sm"><Plus className="w-4 h-4" /> Nouvelle facture</button>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-2 mb-5 text-sm">
        <span className="flex items-center gap-1 text-muted-foreground self-center"><Filter className="w-4 h-4" /> Filtres</span>
        <label className="flex flex-col"><span className="label-mono mb-1">Du</span><input type="date" className={inputCls + " max-w-[160px]"} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="billing-from" /></label>
        <label className="flex flex-col"><span className="label-mono mb-1">Au</span><input type="date" className={inputCls + " max-w-[160px]"} value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="billing-to" /></label>
        <select className={inputCls + " max-w-[190px]"} value={category} onChange={(e) => setCategory(e.target.value)} data-testid="billing-category"><option value="">Toutes familles</option>{INVOICE_CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}</select>
        <select className={inputCls + " max-w-[190px]"} value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="billing-company"><option value="">Toutes compagnies</option>{companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
        <select className={inputCls + " max-w-[190px]"} value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} data-testid="billing-vehicle"><option value="">Tous véhicules</option>{vehicles.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}</select>
        {(category || companyId || vehicleId || dateFrom !== `${yr}-01-01` || dateTo !== todayStr) && <button onClick={() => { setDateFrom(`${yr}-01-01`); setDateTo(todayStr); setCategory(""); setCompanyId(""); setVehicleId(""); }} data-testid="billing-reset" className="text-xs text-muted-foreground hover:text-foreground self-center underline pb-2">Réinitialiser</button>}
      </div>

      {stats && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <div className="glass rounded-lg p-4" data-testid="billing-total"><p className="label-mono">Total global</p><p className="text-2xl font-extrabold data mt-1 brand-orange">{money(total)}</p></div>
            <div className="glass rounded-lg p-4"><p className="label-mono">Factures</p><p className="text-2xl font-extrabold data mt-1">{stats.count}</p></div>
            <div className="glass rounded-lg p-4"><p className="label-mono">Familles</p><p className="text-2xl font-extrabold data mt-1">{stats.by_category.length}</p></div>
            <div className="glass rounded-lg p-4"><p className="label-mono">Véhicules facturés</p><p className="text-2xl font-extrabold data mt-1">{stats.by_vehicle.length}</p></div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
            <div className="glass rounded-lg p-4">
              <p className="label-mono mb-2">Coût par famille</p>
              <div className="space-y-1.5" data-testid="billing-cat-table">
                {catData.length === 0 && <p className="text-xs text-muted-foreground">Aucune donnée.</p>}
                {catData.map((c, i) => { const pct = total ? Math.round((c.value / total) * 100) : 0; return (
                  <div key={i} data-testid={`billing-cat-${i}`}>
                    <div className="flex items-center justify-between text-xs mb-0.5"><span className="font-semibold">{c.name}</span><span className="data">{money(c.value)} · {pct}%</span></div>
                    <div className="h-2 rounded-full bg-secondary/60 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, background: c.fill }} /></div>
                  </div>
                ); })}
              </div>
            </div>
            <div className="glass rounded-lg p-4"><p className="label-mono mb-2">Dépenses par mois</p><ResponsiveContainer width="100%" height={200}><BarChart data={stats.by_month}><XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip formatter={(v) => money(v)} /><Bar dataKey="total" fill="#17A2B8" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer></div>
          </div>
        </>
      )}

      <div className="border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50"><tr className="label-mono text-left"><th className="px-3 py-2">Date</th><th className="px-3 py-2">Véhicule</th><th className="px-3 py-2">Famille</th><th className="px-3 py-2">Fournisseur</th><th className="px-3 py-2">N°</th><th className="px-3 py-2">Montant</th><th className="px-3 py-2">PJ</th><th /></tr></thead>
          <tbody className="data">
            {visibleRows.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">Aucune facture sur la période</td></tr>}
            {visibleRows.map((r) => (
              <tr key={r.id} className="border-t border-border" data-testid={`billing-row-${r.id}`}>
                <td className="px-3 py-2">{r.date ? r.date.slice(0, 10) : "—"}</td>
                <td className="px-3 py-2 font-semibold">{r.vehicle_name || "—"}</td>
                <td className="px-3 py-2">{INVOICE_CATEGORIES.find((c) => c.v === r.category)?.l || r.category}</td>
                <td className="px-3 py-2">{r.supplier || "—"}</td>
                <td className="px-3 py-2">{r.invoice_number || "—"}</td>
                <td className="px-3 py-2 font-semibold">{money(r.amount)}</td>
                <td className="px-3 py-2">{r.file_path ? <a href={fileUrl(r.file_path)} target="_blank" rel="noreferrer" className="text-[#2ec5db]">Voir</a> : "—"}</td>
                <td className="px-3 py-2 text-right"><div className="flex items-center justify-end gap-1"><EditBtn onClick={() => setEditing(r)} testid={`billing-edit-${r.id}`} /><DelBtn onClick={() => del(r.id)} /></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {adding && <InvoiceForm vehicles={vehicles} onSaved={load} onClose={() => setAdding(false)} />}
      {editing && <InvoiceForm vehicles={vehicles} invoice={editing} onSaved={load} onClose={() => setEditing(null)} />}
    </div>
  );
}

// Grouped sidebar shell used by admin pages
export function AdminShell({ title, icon: Icon, groups, active, setActive, children }) {
  return (
    <div>
      <div className="mb-6"><p className="label-mono">Configuration</p><h1 className="text-2xl sm:text-3xl font-extrabold flex items-center gap-3">{Icon && <Icon className="w-7 h-7 brand-teal" />} {title}</h1></div>
      <div className="flex flex-col lg:flex-row gap-6">
        <aside className="lg:w-64 shrink-0 lg:sticky lg:top-20 self-start w-full">
          <div className="lg:max-h-[calc(100vh-8rem)] overflow-y-auto pr-1 space-y-4 border border-border rounded-lg p-3 bg-secondary/60">
            {groups.map((g) => (
              <div key={g.group}>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold px-2 mb-1">{g.group}</p>
                <div className="space-y-0.5">
                  {g.items.map((it) => (
                    <button key={it.k} onClick={() => setActive(it.k)} data-testid={`admin-nav-${it.k}`}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${active === it.k ? "bg-[#17A2B8]/15 text-[#2ec5db]" : "text-muted-foreground hover:text-foreground hover:bg-black/5"}`}>
                      {it.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </aside>
        <div className="flex-1 min-w-0">{children}</div>
      </div>
    </div>
  );
}

